import React, { useCallback, useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import Button from '../../../Button';
import { Types as ButtonTypes } from '../../../Button/Button';
import Modal from '../../../Modal';
import AttachmentList2 from '../AttachmentList2';
import Loader from '@components/CRM/Common/Loader';
import highlightWords from './highlightWords';
import { TEMPLATES_SEARCH } from '@constants/chains';

export const TEMPLATE_GROUPS = {
  COMMON: 'common',
  PERSONAL: 'personal',
};

export const AVAILABLE_TEMPLATE_GROUPS = [];

// тут хз что происходит, невозможно точно определить тип
export type EmailTemplateColumnType =
  | Array<{
      id: number;
      value: string;
      image_url?: string;
    }>
  | {
      [key: string]: {
        id: string | number;
        value: string;
      };
    }
  | {
      id: string | number;
      value: string;
      image_url?: string;
    };

export type FoundMailTemplateType = {
  id: number;
  title: string;
  groupTitle?: string;
  snippet?: string;
};

export type FoundMailTemplatePreviewType = {
  id: number;
  value: string;
  image_url?: string;
};

export type MailTemplatesSearchType = {
  query: string;
  words: string[];
  items: FoundMailTemplateType[];
  total: number;
  isLoading: boolean;
  isError: boolean;
  selectedId: number | null;
  // последний загруженный шаблон: пока грузится новый выбранный, здесь остаётся прежний
  selectedTemplate: FoundMailTemplatePreviewType | null;
};

export interface IEmailTemplateForm {
  className?: string;
  isOpen?: boolean;
  columns: EmailTemplateColumnType[];
  activeTemplateGroup?: string;
  search?: MailTemplatesSearchType;
  onClose: () => void;
  onSubmit: (column: EmailTemplateColumnType) => void;
  onSelectRow: (
    id: number | string,
    parsedIndexColumn: number,
    parsedIndexRow: number
  ) => void;
  onSelectGroup: (group: string) => void;
  onSearch?: (query: string) => void;
  onSelectFound?: (id: number) => void;
  onResetSearch?: () => void;
}

interface ITemplateColumn {
  rows: EmailTemplateColumnType;
  columnIndex?: number;
  onSelectRow: (event: any) => void;
  selectedRowIndex?: number | null;
  isWide?: boolean;
}

const TemplateColumn = ({
  rows = [],
  columnIndex = 0,
  onSelectRow,
  selectedRowIndex = null,
  isWide = false,
}: ITemplateColumn) => (
  <StyledColumn $isWide={isWide}>
    {Array.isArray(rows) ? (
      rows.map(({ value, id }, rowIndex) => (
        <StyledRow
          key={id}
          data-id={id}
          data-index-column={columnIndex}
          data-index-row={rowIndex}
          onClick={onSelectRow}
          selected={selectedRowIndex === rowIndex}
        >
          {value}
        </StyledRow>
      ))
    ) : (
      <StyledPreviewRow>
        <StyledPreview dangerouslySetInnerHTML={{ __html: rows.value }} />
        {rows.image_url && (
          <StyledPreviewAttachments list={[{ url: rows.image_url, size: 0 }]} />
        )}
      </StyledPreviewRow>
    )}
  </StyledColumn>
);

interface ISearchResultsColumn {
  search: MailTemplatesSearchType;
  onSelect: (id: number) => void;
}

const SearchResultsColumn = ({ search, onSelect }: ISearchResultsColumn) => {
  const { items, words, total, isLoading, isError, selectedId } = search;

  if (isLoading && !items.length) {
    return (
      <StyledColumn $isWide>
        <StyledSearchLoader>
          <Loader />
        </StyledSearchLoader>
      </StyledColumn>
    );
  }

  if (isError || !items.length) {
    return (
      <StyledColumn $isWide>
        <StyledSearchMessage>
          {isError
            ? 'Не удалось выполнить поиск, попробуйте ещё раз'
            : 'Ничего не найдено'}
        </StyledSearchMessage>
      </StyledColumn>
    );
  }

  return (
    <StyledColumn $isWide>
      {items.map(({ id, title, groupTitle, snippet }) => (
        <StyledFoundRow
          key={id}
          $selected={id === selectedId}
          onClick={() => onSelect(id)}
        >
          <StyledFoundTitle>{highlightWords(title, words)}</StyledFoundTitle>
          {groupTitle && <StyledFoundGroup>{groupTitle}</StyledFoundGroup>}
          {snippet && (
            <StyledFoundSnippet>
              {highlightWords(snippet, words)}
            </StyledFoundSnippet>
          )}
        </StyledFoundRow>
      ))}
      {total > items.length && (
        <StyledSearchMessage>
          {`Показаны первые ${items.length} из ${total}, уточните запрос`}
        </StyledSearchMessage>
      )}
    </StyledColumn>
  );
};

const EmailTemplateForm = ({
  columns = [],
  onClose,
  onSubmit,
  onSelectRow,
  onSelectGroup,
  className = '',
  activeTemplateGroup = '',
  isOpen = false,
  search,
  onSearch = () => null,
  onSelectFound = () => null,
  onResetSearch = () => null,
  ...rest
}: IEmailTemplateForm) => {
  const [selectedRowList, setSelectedRowList] = useState<Array<number>>([]);
  const [lastSelectedRowId, setLastSelectedRowId] = useState(null);
  const [isLastColumnSelected, setIsLastColumnSelected] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const searchQuery = search?.query || '';
  // поиск заменяет дерево групп, только когда запрос достаточно длинный
  const activeSearch =
    search && searchQuery.trim().length >= TEMPLATES_SEARCH.MIN_QUERY_LENGTH
      ? search
      : null;
  const isShortSearchQuery = !!searchQuery.trim() && !activeSearch;
  // вставить можно только тот шаблон, что выбран в списке, а не прежний, который виден, пока выбранный грузится
  const loadedFoundTemplate =
    activeSearch?.selectedTemplate &&
    activeSearch.selectedTemplate.id === activeSearch.selectedId
      ? activeSearch.selectedTemplate
      : null;

  const handleSelectRow = useCallback(
    event => {
      const data = event.target.dataset;
      const { id, indexColumn, indexRow } = data;
      const parsedIndexColumn = parseInt(indexColumn, 10);
      const parsedIndexRow = parseInt(indexRow, 10);
      setLastSelectedRowId(id);

      const newSelectedRowList = selectedRowList.slice(
        0,
        parsedIndexColumn + 1
      );

      newSelectedRowList[parsedIndexColumn] = parsedIndexRow;
      setSelectedRowList(newSelectedRowList);

      onSelectRow(id, parsedIndexColumn, parsedIndexRow);
    },
    [onSelectRow, selectedRowList]
  );

  const handleSelectGroup = useCallback(
    event => {
      const data = event.target.dataset;
      const { group } = data;
      onSelectGroup(group);
    },
    [onSelectGroup]
  );

  const handleClose = useCallback(() => {
    onResetSearch();
    onClose();
  }, [onResetSearch, onClose]);

  const handleSubmit = useCallback(
    event => {
      event.preventDefault();
      const template = activeSearch
        ? loadedFoundTemplate
        : columns[columns.length - 1];

      if (template) onSubmit(template);
      handleClose();
    },
    [onSubmit, handleClose, columns, activeSearch, loadedFoundTemplate]
  );

  const handleSearchChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      // модалка рендерится в тулбаре Editor, а он вешает onChange формы сообщения
      // на свой div: без этого событие всплывёт через портал и заменит тело письма
      event.stopPropagation();
      onSearch(event.target.value);
    },
    [onSearch]
  );

  const handleSearchKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      // Enter в поле не должен отправлять форму с выбранным шаблоном
      if (event.key === 'Enter') event.preventDefault();
      if (event.key === 'Escape' && searchQuery) {
        event.preventDefault();
        onResetSearch();
      }
    },
    [onResetSearch, searchQuery]
  );

  useEffect(() => {
    setIsLastColumnSelected(
      !!lastSelectedRowId && !Array.isArray(columns[columns.length - 1])
    );
  }, [columns]);

  useEffect(() => {
    if (isOpen) searchInputRef.current?.focus();
  }, [isOpen]);

  return (
    <StyledPopup isOpen={isOpen}>
      <StyledForm onSubmit={handleSubmit} className={className} {...rest}>
        <StyledTitle>Шаблоны</StyledTitle>
        {search && (
          <StyledSearch>
            <StyledSearchInput
              ref={searchInputRef}
              value={searchQuery}
              placeholder="Поиск по названию и тексту шаблона"
              onChange={handleSearchChange}
              onKeyDown={handleSearchKeyDown}
            />
            {searchQuery && (
              <StyledClearButton
                type="button"
                aria-label="Очистить поиск"
                onClick={onResetSearch}
              >
                ✕
              </StyledClearButton>
            )}
          </StyledSearch>
        )}
        {isShortSearchQuery && (
          <StyledSearchHint>
            {`Для поиска введите хотя бы ${TEMPLATES_SEARCH.MIN_QUERY_LENGTH} символа`}
          </StyledSearchHint>
        )}
        <StyledTypeTabs>
          {AVAILABLE_TEMPLATE_GROUPS.map(({ value, title }) => (
            <StyledTypeTab
              key={value}
              onClick={handleSelectGroup}
              data-group={value}
              selected={value === activeTemplateGroup}
            >
              {title}
            </StyledTypeTab>
          ))}
        </StyledTypeTabs>
        <StyledTable>
          {activeSearch ? (
            <>
              <SearchResultsColumn
                search={activeSearch}
                onSelect={onSelectFound}
              />
              {activeSearch.selectedTemplate && (
                <TemplateColumn rows={activeSearch.selectedTemplate} isWide />
              )}
            </>
          ) : (
            columns &&
            columns.map((column, index) => (
              <TemplateColumn
                key={index}
                selectedRowIndex={selectedRowList[index]}
                onSelectRow={handleSelectRow}
                columnIndex={index}
                rows={column}
              />
            ))
          )}
        </StyledTable>
        <StyledControl>
          <StyledButton
            type={ButtonTypes.SUBMIT}
            disabled={activeSearch ? !loadedFoundTemplate : !isLastColumnSelected}
          >
            Использовать
          </StyledButton>
          <StyledCancelButton onClick={handleClose}>Отмена</StyledCancelButton>
        </StyledControl>
      </StyledForm>
    </StyledPopup>
  );
};

const StyledPopup = styled(Modal)`
  display: ${({ isOpen }) => (isOpen ? 'flex' : 'none')};
  align-items: center;
  justify-content: center;
`;

const StyledForm = styled.form`
  background: #ffffff;
  border-radius: 6px;
  padding: 10px;
`;

const StyledTypeTabs = styled.ul`
  display: flex;
  margin-top: 15px;
`;

const StyledTypeTab = styled.li<{ selected: boolean }>`
  display: flex;
  cursor: pointer;
  padding: 10px 15px;
  border: 1px solid #007aff;
  box-sizing: border-box;
  border-radius: 18px;
  background: ${({ selected }) => (selected ? '#007AFF' : 'transparent')};
  color: ${({ selected }) => (selected ? '#FFFFFF' : '#007AFF')};
  margin-right: 10px;

  &:last-of-type {
    margin-right: 0;
  }
`;

const StyledTitle = styled.p`
  font-size: 14px;
  line-height: 18px;
  font-weight: bold;
  color: #17181a;
`;

const StyledButton = styled(Button)`
  border-radius: 4px;
  color: white;
  background-color: #007aff;
  font-size: 14px;
  height: 42px;
  line-height: 24px;
  min-height: 25px;
  min-width: 25px;
  padding: 0;
  position: relative;
  text-align: center;

  ${props =>
    props.disabled &&
    `
    background: #c2c5cc;
    color: white;
  `}
`;

const StyledCancelButton = styled(Button)`
  border: none;
  border-radius: 4px;
  color: #4d4d4d;
  background-color: transparent;
  font-weight: normal;
  font-size: 14px;
  height: 42px;
  line-height: 24px;
  min-height: 25px;
  min-width: 25px;
  padding: 0;
  position: relative;
  text-align: center;
`;

const StyledControl = styled.div`
  display: flex;
`;

const StyledTable = styled.div`
  display: flex;
  margin-top: 10px;
  margin-bottom: 80px;
`;

// в режиме поиска колонок две, а не три, поэтому они шире
const StyledColumn = styled.ul<{ $isWide?: boolean }>`
  display: flex;
  max-height: 400px;
  min-width: ${({ $isWide }) => ($isWide ? 460 : 300)}px;
  max-width: ${({ $isWide }) => ($isWide ? 540 : 400)}px;
  overflow: auto;
  flex-direction: column;
  border-right: 1px solid #e9ebee;

  &:last-of-type {
    border-right: none;
  }
`;

const StyledRow = styled.li<{ selected?: boolean }>`
  display: flex;
  font-size: 14px;
  line-height: 17px;
  cursor: pointer;
  color: #17181a;
  font-weight: ${({ selected }) => (selected ? 'bold' : 'normal')};
  background: ${({ selected }) => (selected ? '#DCEDFF' : 'white')};
  padding: 15px 10px;
`;

const StyledPreviewRow = styled(StyledRow)`
  flex-direction: column;
`;

const StyledPreview = styled.div`
  flex-direction: column;
  white-space: pre-line;
  overflow-y: auto;
  font-size: 14px;
  line-height: 17px;
  color: #17181a;
`;

const StyledPreviewAttachments = styled(AttachmentList2)`
  margin-top: 15px;
`;

const StyledSearch = styled.div`
  position: relative;
  margin-top: 10px;
`;

const StyledSearchInput = styled.input`
  width: 100%;
  height: 38px;
  padding: 10px 34px 10px 10px;
  border: 1px solid #e9ebee;
  border-radius: 4px;
  font-size: 14px;
  color: #17181a;
  outline: none;

  &:focus {
    border-color: #007aff;
  }
`;

const StyledClearButton = styled.button`
  position: absolute;
  top: 50%;
  right: 6px;
  transform: translateY(-50%);
  width: 24px;
  height: 24px;
  padding: 0;
  border: none;
  background: transparent;
  color: #8a8f99;
  font-size: 14px;
  cursor: pointer;

  &:hover {
    color: #17181a;
  }
`;

const StyledSearchHint = styled.p`
  margin-top: 6px;
  font-size: 12px;
  color: #8a8f99;
`;

const StyledFoundRow = styled.li<{ $selected: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 12px 10px;
  cursor: pointer;
  background: ${({ $selected }) => ($selected ? '#DCEDFF' : 'white')};

  & + & {
    border-top: 1px solid #e9ebee;
  }

  mark {
    background: #ffe58f;
    color: inherit;
  }
`;

const StyledFoundTitle = styled.span`
  font-size: 16px;
  line-height: 20px;
  color: #17181a;
`;

const StyledFoundGroup = styled.span`
  font-size: 14px;
  line-height: 17px;
  color: #8a8f99;
`;

const StyledFoundSnippet = styled.span`
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  font-size: 14px;
  line-height: 18px;
  color: #4d4d4d;
`;

const StyledSearchLoader = styled.li`
  padding: 30px 0;
`;

const StyledSearchMessage = styled.li`
  padding: 15px 10px;
  font-size: 14px;
  line-height: 17px;
  color: #8a8f99;
`;

TemplateColumn.defaultProps = {
  rows: [],
  columnIndex: 0,
  selectedRowIndex: null,
  onSelectRow: Function.prototype,
};

export default EmailTemplateForm;
